/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Modal,
  Button,
  Spin,
  Form,
  DatePicker,
  Input,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import { getTipoDeConceptoDeInfraccion } from 'api/recaudacion/tipoDeConceptosDeInfraccion';
import { onError } from 'utils/handlers';
import { postConceptosDeInfraccion } from 'api/recaudacion/conceptosDeInfracciones';
import { decimalNormalizer, toInteger } from 'utils/normalizers';
import { patchInfraccion } from 'api/recaudacion/infracciones';

const ModalAgregarConceptos = ({
  onCancel,
  visible,
  selectedConcepto,
  infraccion,
  callback,
  existentes,
  changeStatus,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [match, setMatch] = useState();

  useEffect(() => {
    let mounted = true;
    if (visible && selectedConcepto) {
      const _fetchData = async () => {
        setLoading(true);
        const _data = await getTipoDeConceptoDeInfraccion(selectedConcepto);
        if (mounted) {
          setMatch(_data);
          setLoading(false);
          form.setFieldsValue({
            importe: _data.es_importe_capturable ? undefined : _data.importe_de_moneda_fija_inicial,
            cantidad: 1,
          });
        }
      };
      _fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [selectedConcepto, visible]);

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    cantidad: [
      requiredRule,
      {
        validator: async (_, val) => {
          if ((parseInt(val, 10) + existentes) > match.maximo_de_concepto_repetido) {
            throw new Error(`Ingrese una cantidad menor al importe máximo repetible (Max: ${match.maximo_de_concepto_repetido - existentes})`);
          }
        },
      },
    ],
    importe: [
      requiredRule,
      {
        validator: async (_, val) => {
          if (!val) return;
          if (val < match.importe_de_moneda_minima) {
            throw new Error('Ingrese una cantidad mayor al importe mínimo de la infracción');
          }
          if (val > match.importe_de_moneda_maxima) {
            throw new Error('Ingrese una cantidad menor al importe máximo de la infracción');
          }
        },
      },
    ],
  };

  const updateInfraccion = async ({ id, estado_de_la_infraccion }) => {
    try {
      await patchInfraccion(id, { estado_de_la_infraccion });
      changeStatus(estado_de_la_infraccion);
    } catch (error) {
      onError(error);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const { cantidad, ...values } = form.getFieldsValue();
      values.infraccion = infraccion;
      values.clave_de_tipo_de_concepto = match.clave;
      values.descripcion_de_tipo_de_concepto = match.descripcion;
      values.tipo_de_concepto_de_infraccion = match.id;
      values.periodo_fiscal = match.periodo_fiscal;
      values.moneda = match.monedas;
      if (match.clase_de_concepto_de_infraccion === 2) {
        values.examen_medico = match.examen_medico;
      }
      if (match.clase_de_concepto_de_infraccion === 3) {
        values.grua = match.grua;
      }
      if (match.clase_de_concepto_de_infraccion === 4) {
        values.yarda = match.yarda;
      }
      const promises = [];
      for (let i = 0; i < cantidad; i += 1) {
        promises.push(postConceptosDeInfraccion(values, match));
      }
      const saved = await Promise.all(promises);
      if (saved.some((e) => e)) {
        callback();
        form.resetFields();
      }
      // SE ACTUALIZA EL ESTADO DE LA INFRACCION A ACTIVA (estado_de_la_infraccion = 3);
      await updateInfraccion({ id: infraccion, estado_de_la_infraccion: 3 });
      onCancel();
      setLoading(false);
    } catch (error) {
      onError(error);
    }
  };

  const controls = [
    {
      text: 'Cancelar',
      icon: <ArrowLeftOutlined />,
      onClick: () => {
        onCancel();
        setMatch();
        form.resetFields();
      },
    },
    {
      text: 'Guardar',
      icon: <SaveOutlined />,
      onClick: () => onFinish(),
    },
  ];

  const disabledCantidad = !match?.es_concepto_repetible
  || (match.maximo_de_concepto_repetido - existentes === 1);

  return (
    <Modal
      footer={null}
      closable={false}
      open={visible}
      keyboard={!loading}
      maskClosable={!loading}
      centered
      title={(
        <FormSubmitControls
          label="Agregar Concepto"
          controls={controls}
          loading={loading}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        <Col span={24}>
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
          >
            <Col span={24}>
              <Form.Item
                name="importe"
                label={match?.es_importe_capturable
                  ? `Importe (MIN: ${match.importe_de_moneda_minima} - MAX: ${match.importe_de_moneda_maxima})`
                  : 'Importe'}
                rules={match?.es_importe_capturable ? rules.importe : null}
                normalize={(val) => decimalNormalizer(val)}
              >
                <Input disabled={loading || !match?.es_importe_capturable} />
              </Form.Item>
            </Col>
            {match?.capturable_de_fecha_inicial && (
              <Col span={24}>
                <Form.Item
                  label="Fecha inicial"
                  name="fecha_inicial"
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            )}
            {match?.capturable_de_fecha_final && (
              <Col span={24}>
                <Form.Item
                  label="Fecha final"
                  name="fecha_final"
                >
                  <DatePicker
                    placeholder=""
                    format="DD/MM/YYYY"
                    disabledDate={(d) => d.isBefore(form.getFieldValue('fecha_inicial'))}
                    disabled={loading}
                  />
                </Form.Item>
              </Col>
            )}
            {match?.capturable_de_nota_de_concepto && (
              <Col span={24}>
                <Form.Item
                  name="nota_de_concepto"
                  label="Nota de Concepto"
                >
                  <Input.TextArea autoSize={{ minRows: 2, maxRows: 2 }} />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                name="cantidad"
                label={disabledCantidad ? 'Cantidad' : `Cantidad (Max: ${match.maximo_de_concepto_repetido}, Existentes: ${existentes})`}
                rules={rules.cantidad}
                normalize={toInteger}
              >
                <Input
                  disabled={disabledCantidad}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
      </Spin>
    </Modal>
  );
};

ModalAgregarConceptos.propTypes = {
  onCancel: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  //   _cargo: PropTypes.shape({
  //     id: PropTypes.number.isRequired,
  //     tipo_de_cargo: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  //   }),
  selectedConcepto: PropTypes.number,
  visible: PropTypes.bool,
  padron: PropTypes.shape({
    id: PropTypes.number.isRequired,
    padron: PropTypes.number,
    tipo_de_padron: PropTypes.number,
  }),
  infraccion: PropTypes.number,
  existentes: PropTypes.number.isRequired,
  changeStatus: PropTypes.func,
};

ModalAgregarConceptos.defaultProps = {
  visible: false,
  padron: null,
  selectedConcepto: null,
  infraccion: null,
  changeStatus: () => {},
};

export default ModalAgregarConceptos;
